import {
  RegistrationStateSchema,
  Rfc3339MillisSchema,
  UuidSchema,
} from "@mind/contracts/browser";
import type {
  AuthenticationExtensionsClientInputs,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { z } from "zod";

const Base64UrlSchema = z.string().regex(/^[A-Za-z0-9_-]+$/);
const CredentialTransportSchema = z.enum([
  "ble",
  "cable",
  "hybrid",
  "internal",
  "nfc",
  "smart-card",
  "usb",
]);
const CredentialDescriptorSchema = z
  .object({
    id: Base64UrlSchema,
    type: z.literal("public-key"),
    transports: z.array(CredentialTransportSchema).optional(),
  })
  .strict();
const CredentialHintSchema = z.enum(["hybrid", "security-key", "client-device"]);
const UserVerificationSchema = z.enum(["discouraged", "preferred", "required"]);
const CredentialExtensionsSchema = z.custom<AuthenticationExtensionsClientInputs>(
  (value) => value !== null && typeof value === "object" && !Array.isArray(value),
  "WebAuthn extensions must be an object",
);

export const PasskeyRegistrationOptionsSchema = z
  .object({
    rp: z.object({ id: z.string().min(1).optional(), name: z.string().min(1) }).strict(),
    user: z
      .object({ id: Base64UrlSchema, name: z.string().min(1), displayName: z.string().min(1) })
      .strict(),
    challenge: Base64UrlSchema,
    pubKeyCredParams: z
      .array(z.object({ type: z.literal("public-key"), alg: z.number().int() }).strict())
      .min(1),
    timeout: z.number().positive().optional(),
    excludeCredentials: z.array(CredentialDescriptorSchema).optional(),
    authenticatorSelection: z
      .object({
        authenticatorAttachment: z.enum(["platform", "cross-platform"]).optional(),
        requireResidentKey: z.boolean().optional(),
        residentKey: z.enum(["discouraged", "preferred", "required"]).optional(),
        userVerification: UserVerificationSchema.optional(),
      })
      .strict()
      .optional(),
    hints: z.array(CredentialHintSchema).optional(),
    extensions: CredentialExtensionsSchema.optional(),
    attestation: z.enum(["none", "indirect", "direct", "enterprise"]).optional(),
    attestationFormats: z
      .array(z.enum(["fido-u2f", "packed", "android-safetynet", "android-key", "tpm", "apple", "none"]))
      .optional(),
  })
  .strict()
  .transform((input): PublicKeyCredentialCreationOptionsJSON => {
    const selection = input.authenticatorSelection;
    return {
      rp: input.rp.id === undefined ? { name: input.rp.name } : { id: input.rp.id, name: input.rp.name },
      user: input.user,
      challenge: input.challenge,
      pubKeyCredParams: input.pubKeyCredParams,
      ...(input.timeout === undefined ? {} : { timeout: input.timeout }),
      ...(input.excludeCredentials === undefined
        ? {}
        : {
            excludeCredentials: input.excludeCredentials.map((credential) =>
              credential.transports === undefined
                ? { id: credential.id, type: credential.type }
                : { id: credential.id, type: credential.type, transports: credential.transports },
            ),
          }),
      ...(selection === undefined
        ? {}
        : {
            authenticatorSelection: {
              ...(selection.authenticatorAttachment === undefined
                ? {}
                : { authenticatorAttachment: selection.authenticatorAttachment }),
              ...(selection.requireResidentKey === undefined
                ? {}
                : { requireResidentKey: selection.requireResidentKey }),
              ...(selection.residentKey === undefined ? {} : { residentKey: selection.residentKey }),
              ...(selection.userVerification === undefined
                ? {}
                : { userVerification: selection.userVerification }),
            },
          }),
      ...(input.hints === undefined ? {} : { hints: input.hints }),
      ...(input.attestation === undefined ? {} : { attestation: input.attestation }),
      ...(input.attestationFormats === undefined ? {} : { attestationFormats: input.attestationFormats }),
      ...(input.extensions === undefined ? {} : { extensions: input.extensions }),
    };
  });

export const PasskeyAuthenticationOptionsSchema = z
  .object({
    challenge: Base64UrlSchema,
    timeout: z.number().positive().optional(),
    rpId: z.string().min(1).optional(),
    allowCredentials: z.array(CredentialDescriptorSchema).optional(),
    userVerification: UserVerificationSchema.optional(),
    hints: z.array(CredentialHintSchema).optional(),
    extensions: CredentialExtensionsSchema.optional(),
  })
  .strict()
  .transform((input): PublicKeyCredentialRequestOptionsJSON => ({
    challenge: input.challenge,
    ...(input.timeout === undefined ? {} : { timeout: input.timeout }),
    ...(input.rpId === undefined ? {} : { rpId: input.rpId }),
    ...(input.allowCredentials === undefined
      ? {}
      : {
          allowCredentials: input.allowCredentials.map((credential) =>
            credential.transports === undefined
              ? { id: credential.id, type: credential.type }
              : { id: credential.id, type: credential.type, transports: credential.transports },
          ),
        }),
    ...(input.userVerification === undefined ? {} : { userVerification: input.userVerification }),
    ...(input.hints === undefined ? {} : { hints: input.hints }),
    ...(input.extensions === undefined ? {} : { extensions: input.extensions }),
  }));

export const BrowserSessionSchema = z.union([
  z.object({ authenticated: z.boolean() }).strict(),
  z.null(),
]);
export const AuthenticatedSchema = z.object({ authenticated: z.literal(true) }).strict();
export const SignedOutSchema = z.object({ authenticated: z.literal(false) }).strict();
export const PasswordSignInResultSchema = z.discriminatedUnion("secondFactorRequired", [
  z.object({ authenticated: z.literal(true), secondFactorRequired: z.literal(false) }).strict(),
  z.object({ authenticated: z.literal(false), secondFactorRequired: z.literal(true) }).strict(),
]);

export const SetupStateSchema = z
  .object({ state: RegistrationStateSchema, expiresAt: Rfc3339MillisSchema })
  .strict();
export const PasswordSetupResultSchema = z.object({ state: z.literal("password_set") }).strict();
export const TotpSetupMaterialSchema = z
  .object({
    totpUri: z.string().url().refine((value) => value.startsWith("otpauth://totp/"), "TOTP URI required"),
    backupCodes: z.array(z.string().min(1).max(100)).min(1).max(100),
  })
  .strict();
export const SetupVerifiedSchema = z.object({ verified: z.literal(true) }).strict();
export const SetupCompletedSchema = z.object({ completed: z.literal(true) }).strict();
export const SimulationConsumedSchema = z
  .object({ effectiveAccountId: UuidSchema, expiresAt: Rfc3339MillisSchema })
  .strict();
export const SimulationEndedSchema = z.object({ revoked: z.literal(true) }).strict();

export type SetupState = z.infer<typeof SetupStateSchema>;
export type TotpSetupMaterial = z.infer<typeof TotpSetupMaterialSchema>;
